import React, { useContext, useEffect, useState } from 'react'
import clsx from 'clsx'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { loadStripe } from '@stripe/stripe-js'

import Payment from '../Payment/Payment'
import EmbeddedCheckoutPanel from '../Payment/EmbeddedCheckoutPanel'
import PaymentService from '../../../../services/PaymentService'
import DonutChart from '../../../charts/DonutChart'
import { ToastsContext } from '../../../../contexts/ToastsContext'
import { UserContext } from '../../../../contexts/UserContext'
import { useSettings } from '../../../../hooks/useSettings'
import {
  getPrimaryClass,
  getHoverClass,
  getTextClass,
} from '../../../../utils/themeUtils'

import ConsumptionChart from './ConsumptionChart'

const Consumption = ({ activeMenu, setActiveMenu }) => {
  const [stripePromise, setStripePromise] = useState(null)
  const [clientSecret, setClientSecret] = useState('')
  // Checkout incorporata (Managed Payments): {publishableKey, clientSecret} della
  // Checkout Session. null = vecchio flusso PaymentIntent + Payment Element.
  const [embedded, setEmbedded] = useState(null)
  const [packages, setPackages] = useState([])
  // false finche' il listino non e' arrivato: un elenco vuoto vuol dire "nessun
  // pacchetto in vendita" (senza price Stripe non si vende), non "sto caricando".
  const [packagesLoaded, setPackagesLoaded] = useState(false)
  const { t } = useTranslation('payments')

  const { user } = useContext(UserContext)

  const {
    handleSubmit,
    formState: { isSubmitting },
    getValues,
    setValue,
    watch,
  } = useForm({ defaultValues: { credits: null } })

  const selectedCredits = watch('credits')

  const { setToasts } = useContext(ToastsContext)
  const { isDarkMode, currentTheme } = useSettings()

  // Carica il listino pacchetti dal backend (unica fonte di verità) e
  // preseleziona il pacchetto intermedio se disponibile.
  useEffect(() => {
    let active = true
    PaymentService.getPackages()
      .then((res) => {
        if (!active) return
        const list = res.data || []
        setPackages(list)
        setPackagesLoaded(true)
        if (list.length > 0) {
          const mid = list[Math.floor(list.length / 2)]
          setValue('credits', mid.credits)
        }
      })
      .catch((e) =>
        setToasts([
          {
            message:
              e?.response?.data?.message ||
              t('rechargingStation.consumption.initError'),
            type: 'error',
          },
        ])
      )
    return () => {
      active = false
    }
  }, [setValue, setToasts, t])

  const selectedPackage = packages.find((p) => p.credits === selectedCredits)

  // I numeri veri del ledger dei crediti. undefined = in caricamento,
  // null = non disponibili. Fino al 28/09 qui c'erano valori fissi (consumato
  // 5, un grafico di novembre 2024): il cliente vedeva consumi non suoi.
  const [summary, setSummary] = useState(undefined)

  useEffect(() => {
    let vivo = true
    PaymentService.getConsumptionSummary()
      .then((r) => vivo && setSummary(r.data))
      .catch(() => vivo && setSummary(null))
    return () => {
      vivo = false
    }
  }, [])

  const formatXpu = (value) => Number(value || 0).toFixed(1)

  const donutData = summary
    ? [
        { name: 'Consumed', value: summary.consumed },
        // La ciambella non disegna un settore negativo: un saldo sotto zero
        // resta scritto nel numero accanto.
        { name: 'Remaining', value: Math.max(0, summary.remaining) },
      ]
    : []

  const chartData =
    summary && summary.daily.length
      ? [
          ...summary.daily.map((d) => ({
            division: 'Consumption',
            x: new Date(d.date),
            y: d.cumulative,
          })),
          // Il disponibile complessivo, dal primo consumo a oggi.
          {
            division: 'Credits',
            x: new Date(summary.daily[0].date),
            y: summary.total,
          },
          { division: 'Credits', x: new Date(), y: summary.total },
        ]
      : []

  const onSubmit = async (data) => {
    try {
      if (!data.credits) {
        throw new Error(t('rechargingStation.consumption.validation.required'))
      }

      const paymentConfig = await PaymentService.getPaymentConfig()
      const { publishableKey, checkoutMode } = paymentConfig.data

      if (!publishableKey) {
        throw new Error('Stripe publishable key not found in payment config')
      }

      // Managed Payments (spec digiscan-web 2026-10-22): il server crea la
      // Checkout Session del pacchetto, qui la si incorpora. Il prezzo lo decide
      // lui; noi mandiamo solo i crediti e l'origine per il ritorno.
      if (checkoutMode === 'managed_payments') {
        const session = await PaymentService.createCreditsCheckout(
          data.credits,
          window.location.origin
        )
        const { clientSecret: secret, publishableKey: key } = session.data
        if (!secret) {
          throw new Error('Client secret not found in checkout session response')
        }
        setEmbedded({ clientSecret: secret, publishableKey: key || publishableKey })
        setActiveMenu('Payment')
        return
      }

      const stripe = await loadStripe(publishableKey)

      if (!stripe) {
        throw new Error('Failed to initialize Stripe')
      }

      setStripePromise(stripe)

      const paymentIntent = await PaymentService.createPaymentIntent(
        user.email,
        data.credits
      )

      const { clientSecret } = paymentIntent.data

      if (!clientSecret) {
        throw new Error('Client secret not found in payment intent response')
      }

      setClientSecret(clientSecret)
      setActiveMenu('Payment')
    } catch (e) {
      const errorMessage =
        e?.response?.data?.message ||
        e?.message ||
        t('rechargingStation.consumption.initError')
      setToasts([{ message: errorMessage, type: 'error' }])
    }
  }

  const closeEmbedded = () => {
    setEmbedded(null)
    setActiveMenu('Consumption')
  }

  return activeMenu === 'Payment' && embedded ? (
    <EmbeddedCheckoutPanel
      publishableKey={embedded.publishableKey}
      clientSecret={embedded.clientSecret}
      onBack={closeEmbedded}
    />
  ) : activeMenu === 'Payment' ? (
    <Payment
      orderDetails={{
        credits: getValues('credits'),
        amountCents: selectedPackage?.amountCents,
        currency: selectedPackage?.currency,
      }}
      setActiveMenu={setActiveMenu}
      stripePromise={stripePromise}
      clientSecret={clientSecret}
    />
  ) : (
    // Una colonna sola: quel che si legge sta in alto e scorre, quel che si fa
    // sta in fondo e non si muove. Prima il riepilogo e il grafico avevano
    // altezze minime rigide che sommate superavano lo spazio disponibile, e su
    // schermo basso finivano sopra i pulsanti (ticket 6a8c1f4f). Stesso
    // difetto, e stessa correzione, della stazione dentro LeaRes: i due
    // componenti sono copie l'uno dell'altro.
    <div className="flex flex-col h-full min-h-0 gap-4">
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4 pr-1">
        {/* Riepilogo: ciambella compatta accanto ai numeri */}
        <div className="flex items-center gap-6 flex-shrink-0">
          <div className="w-[150px] aspect-square flex-shrink-0 pointer-events-none">
            {summary && (
              <DonutChart
                chartData={donutData}
                renderDependencies={[summary]}
                isPDF={false}
                indent={1}
              />
            )}
          </div>
          <div className="flex flex-col justify-center gap-1">
            <div className={clsx('font-light', getTextClass(currentTheme))}>
              <span className="font-bold">
                {t('rechargingStation.consumption.consumed')}{' '}
              </span>
              <span data-testid="consumption-consumed">
                {summary ? formatXpu(summary.consumed) : '…'}
              </span>
              <span className="text-xs">
                {' '}
                {t('rechargingStation.consumption.xpuHours')}
              </span>
            </div>
            <div className={clsx('font-light', getTextClass(currentTheme))}>
              <span className="font-bold">
                {t('rechargingStation.consumption.remaining')}{' '}
              </span>
              <span data-testid="consumption-remaining">
                {summary ? formatXpu(summary.remaining) : '…'}
              </span>
              <span className="text-xs">
                {' '}
                {t('rechargingStation.consumption.xpuHours')}
              </span>
            </div>
            <div className={clsx('font-light', getTextClass(currentTheme))}>
              <span className="font-bold">
                {t('rechargingStation.consumption.total')}{' '}
              </span>
              <span data-testid="consumption-total">
                {summary ? formatXpu(summary.total) : '…'}
              </span>
              <span className="text-xs">
                {' '}
                {t('rechargingStation.consumption.xpuHours')}
              </span>
            </div>
          </div>
        </div>

        {/* Andamento nel tempo: altezza contenuta, l'SVG ha un viewBox e si
            adatta al riquadro. */}
        <div className="h-[220px] flex-shrink-0 pointer-events-none">
          {summary === null && (
            <p className={clsx('text-sm', getTextClass(currentTheme))}>
              {t('rechargingStation.consumption.loadError')}
            </p>
          )}
          {summary && !chartData.length && (
            <p className={clsx('text-sm', getTextClass(currentTheme))}>
              {t('rechargingStation.consumption.noConsumption')}
            </p>
          )}
          {chartData.length > 0 && (
            <ConsumptionChart
              chartId="consumptionDetails"
              chartTitle=""
              chartData={chartData}
              axesNames={{
                x: t('rechargingStation.consumption.chart.date'),
                y: t('rechargingStation.consumption.chart.credits'),
              }}
              chartDimensions={{
                width: 760,
                height: 260,
                margin: {
                  top: 12,
                  right: 24,
                  bottom: 38,
                  left: 52,
                },
              }}
            />
          )}
        </div>
      </div>

      {/* Acquisto: ancorato in fondo, non puo' essere coperto da quel che sta
          sopra. Qui l'acquisto si fa davvero: il portale e' il posto giusto. */}
      <form
        className="flex flex-col gap-3 flex-shrink-0"
        onSubmit={handleSubmit(onSubmit)}
      >
        <span
          className={clsx(
            'text-base font-light leading-none',
            getTextClass(currentTheme)
          )}
        >
          {t('rechargingStation.consumption.addToAccount')}
        </span>
        {packagesLoaded && packages.length === 0 && (
          <p
            data-testid="consumption-no-packs"
            className={clsx('text-sm', getTextClass(currentTheme))}
          >
            {t('rechargingStation.consumption.noPacks')}{' '}
            <a className="underline" href="mailto:accounts@medlea-tech.com">
              accounts@medlea-tech.com
            </a>
          </p>
        )}
        <div className="flex gap-3">
          {packages.map((pkg) => {
            const isSelected = pkg.credits === selectedCredits
            const symbol = pkg.currency === 'eur' ? '€' : '$'
            return (
              <button
                key={pkg.credits}
                type="button"
                onClick={() => setValue('credits', pkg.credits)}
                aria-pressed={isSelected}
                className={clsx(
                  'flex-1 rounded-xl border-2 px-4 py-3 text-left transition duration-100',
                  getTextClass(currentTheme),
                  isSelected
                    ? `${getPrimaryClass(
                        currentTheme
                      )} bg-opacity-30 border-current shadow-[inset_0px_0px_14px_2px_#00000015]`
                    : 'border-current/30 hover:border-current'
                )}
              >
                <span className="block text-lg font-bold leading-tight">
                  {pkg.credits}
                  <span className="text-xs font-light">
                    {' '}
                    {t('rechargingStation.consumption.xpuHours')}
                  </span>
                </span>
                <span className="block text-sm font-light opacity-80">
                  <span className="mr-px">{symbol}</span>
                  {(pkg.amountCents / 100).toFixed(2)}
                </span>
              </button>
            )
          })}
        </div>
        <button
          className={clsx(
            'h-[36px] px-6 text-white font-light rounded-xl border border-current transition duration-100 active:shadow-[inset_0px_0px_14px_5px_#00000020] flex justify-center items-center whitespace-nowrap',
            getPrimaryClass(currentTheme),
            {
              [getHoverClass(currentTheme)]: !isSubmitting && selectedCredits,
              'opacity-50 pointer-events-none':
                isSubmitting || !selectedCredits,
            }
          )}
          type="submit"
        >
          {isSubmitting ? (
            <img
              className="h-[36px]"
              src="/assets/images/button-loader.svg"
              alt=""
            ></img>
          ) : (
            t('rechargingStation.consumption.checkout')
          )}
        </button>
      </form>
    </div>
  )
}

export default Consumption
